/**
 * Per-member chat-storage limits (#758 follow-up).
 *
 * Three layers, resolved by `resolveChatStorageLimit`:
 *
 *   1. the member's own limit   `memberships.chat_storage_limit_bytes`
 *   2. the tenant's per-role    `tenant_settings.chat_storage_role_limits`
 *   3. this module's default    `DEFAULT_ROLE_CHAT_STORAGE`
 *
 * Deliberately the same SHAPE as the AI budget's `DEFAULT_ROLE_CAPS` +
 * `tenant_settings.role_caps` + `resolveRoleCap`, because an admin meets both
 * on the same Settings screens and a second precedence model would be a second
 * thing to learn. The one difference is layer 1: the AI budget's per-member
 * value is an ADJUSTMENT added on top, while a chat-storage limit REPLACES the
 * role's — so it is a plain nullable column rather than a window-keyed blob.
 *
 * **ABSENCE MEANS INHERIT, at every layer, and that is load-bearing.** Putting
 * a member back on their role's default is `chat_storage_limit_bytes = NULL`,
 * never "write the number the default happens to be today": a member left
 * holding 500 because someone typed 500 would silently skip the next change to
 * the role default while the admin believed they had reset them. The UI says
 * the same thing — the Adjust-limit dialog asks "who decides this member's
 * limit" before it offers a number.
 *
 * Browser-safe, so the SPA can render a role's default without a round trip,
 * exactly as it does for `DEFAULT_ROLE_CAPS`. The one import is this package's
 * own byte formatter, for the refusal message at the foot of the file.
 *
 * It does NOT match the storage screens, and this comment used to claim it
 * did (full review 3C, finding 5). Settings renders through `formatBytes`
 * in `features/orgs/ui/budget/bits.tsx`, which carries two decimals where
 * this one carries one: 1 GiB reads "1.00 GB" on the page and "1.0 GB" in
 * a refusal. Cosmetic — both name the same quantity, and nothing compares
 * the strings — but do not reason from the old claim. Unifying them means
 * changing every figure on a designed page, so it is not done here.
 */
import { formatStorageBytes } from './storage-quota.js'

/** Bytes, or `null` for unlimited. Mirrors `BudgetCap`'s two-state meaning. */
export type ChatStorageLimit = number | null

const MB = 1024 * 1024

/**
 * What a role gets when nobody has said otherwise.
 *
 * 500 MB for everyone who can upload; 0 for the two roles that cannot use the
 * agent at all (Season + owner, 2026-10-10 — the same split `DEFAULT_ROLE_CAPS`
 * makes, where guest and noaccess are the only zeros). A role this table does
 * not know resolves to 0, see `resolveChatStorageLimit`.
 *
 * Two things the convergence review flagged about these values, both open and
 * both for the owner rather than for whoever wires this up:
 *
 *   * `viewer` gets 500 MB but is not in `WRITE_PERMS`
 *     (`features/agent/api/may-write-workspace.ts`), so a viewer cannot
 *     create inline bytes at all. A budget for a role that cannot spend it is
 *     either harmless or a sign the role list is wrong — see D1(a), which asks
 *     whether the AGENT's own captures should count, since those are charged
 *     to the viewer and are gated on budget rather than write permission.
 *   * `guest` and `noaccess` are NOT values `memberships.role` takes (its own
 *     comment says `'owner' | 'admin' | 'member' | 'viewer'`). They are copied
 *     from `DEFAULT_ROLE_CAPS`, which is keyed by `RoleId`. So the two zeros
 *     are unreachable through a membership today and the only live defaults
 *     are four identical 500 MBs. Kept for shape-parity with the budget — and
 *     because an unknown role resolving to 0 is the conservative answer
 *     either way — but do not read them as enforcing anything.
 *
 * The VALUE itself (500 MB vs the budget's `null` for these four roles) was
 * SETTLED on 2026-10-10: 500 MB stays, and a member already over it on day
 * one is simply over it — the admin raises their limit. Chosen deliberately
 * over "default to unlimited" and over grandfathering existing members,
 * because the customer count is still small.
 *
 * That choice has a shipping consequence worth knowing before you touch this:
 * "the admin raises their limit" requires the admin screen and the member
 * screen to EXIST. Enforcing this default before they ship means a member
 * loses the attach button with no explanation and no remedy, so the backfill
 * that makes existing bytes count must run after those screens, not with the
 * metering.
 */
export const DEFAULT_ROLE_CHAT_STORAGE: Readonly<Record<string, ChatStorageLimit>> = {
  owner: 500 * MB,
  admin: 500 * MB,
  member: 500 * MB,
  viewer: 500 * MB,
  guest: 0,
  noaccess: 0,
}

/** One role's entry in `tenant_settings.chat_storage_role_limits`. */
export interface ChatStorageRoleEntry {
  /** Bytes, or `null` for an override TO unlimited. The KEY being absent from
   *  the map is what means "not overridden" — never represent that as 0. */
  limitBytes?: ChatStorageLimit
}

/**
 * `bigint` columns arrive from pg as STRINGS, and `'500' + 0` is `'5000'`.
 *
 * INTERNAL, and deliberately not exported: the two places a stored limit comes
 * from mean OPPOSITE things by `null`, so one shared reader is a trap. This
 * does the coercion only; the two exported readers below say what an absent
 * value MEANS.
 */
function coerceStoredBytes(raw: string | number | null | undefined): number | undefined {
  if (raw === null || raw === undefined) return undefined
  const n = typeof raw === 'number' ? raw : Number(raw)
  if (!Number.isFinite(n) || n < 0) return undefined
  return Math.floor(n)
}

/**
 * `memberships.chat_storage_limit_bytes`, where **NULL means INHERIT** — the
 * member has no limit of their own and resolves through their role.
 *
 * Returns `undefined` for NULL, never `null`.
 *
 * A CAPABILITY GAP falls out of that (tenth review, finding 4): the role
 * layer keeps three states — absent / a number / `null` for unlimited — so
 * this column's two states cannot express per-member unlimited. Dropping
 * `null`'s third meaning here was necessary (it collided with "inherit", see
 * below), but the capability went with it.
 *
 * ⚠️ THE GAP IS NOW DELIBERATE, AND WIDER (owner, 2026-10-30): no role may
 * be unlimited either. An earlier version of this paragraph said an admin
 * could make a whole ROLE unlimited and offered that as the workaround —
 * that route is refused now, so nothing in the product can create an
 * unlimited anything. If per-member unlimited is ever wanted it needs a
 * sentinel or a second column AND that decision reversed; it is not a gap
 * waiting to be filled.
 *
 * The split exists because the first version was one exported reader whose
 * docblock called it "THE ONE designated coercion for a stored chat-storage
 * limit" — and reading this column through it yielded `null`, which every
 * other part of this module treats as UNLIMITED. The convergence review caught
 * it before anything consumed the layer. Someone writing enforcement by
 * following that instruction would have given every non-custom member
 * unlimited chat storage — the exact opposite of the documented 500 MB
 * default — and it would have looked like a faithful reading of the docs.
 */
export function readMemberChatStorageLimit(
  raw: string | number | null | undefined,
): number | undefined {
  return coerceStoredBytes(raw)
}

/**
 * One role's value inside `tenant_settings.chat_storage_role_limits`, where
 * **`null` means UNLIMITED** — an explicit override.
 *
 * ⚠️ READ-ONLY NOW: no route will write one (owner, 2026-10-30; `PATCH
 * /:id/roles/chat-storage` refuses it). This reader stays because rows
 * written before that refusal must still read back as unlimited rather than
 * as something else.
 *
 * An earlier version of this line called null "the only way to lift a role
 * whose shipped default is 0". Both halves were wrong: a byte value lifts
 * `guest` or `noaccess` just as well, and unlimited is not settable at all.
 *
 * Returns `null` for an explicit null and `undefined` only for a malformed
 * value, so a caller can still tell "overridden to unlimited" from "not usable,
 * fall through".
 */
export function readRoleChatStorageLimit(
  raw: string | number | null | undefined,
): ChatStorageLimit | undefined {
  if (raw === null) return null
  return coerceStoredBytes(raw)
}

/**
 * The member's effective limit in bytes, or `null` for unlimited.
 *
 * Precedence, highest first — and each layer is skipped only when it holds
 * NOTHING, never when it holds 0 (a deliberate 0 is "cannot upload"):
 *
 *   `member`                a stored per-member limit
 *   `roleOverrides[role]`   present key in the tenant's per-role map
 *   `DEFAULT_ROLE_CHAT_STORAGE[role]`
 *   `0`                     a role this build has not learned
 *
 * Unknown roles resolve to 0, the conservative reading for a membership whose
 * role the code does not know — matching `resolveRoleCap`. `onUnknownRole`
 * lets enforcement log it without this browser-safe module owning a logger.
 * Own-property lookups, so a role named `constructor` is unknown rather than a
 * function.
 */
export function resolveChatStorageLimit(args: {
  role: string
  /** `tenant_settings.chat_storage_role_limits`. */
  roleOverrides?: Record<string, ChatStorageRoleEntry> | null
  /** `memberships.chat_storage_limit_bytes`, as stored (string from pg). */
  member?: string | number | null
  onUnknownRole?: (role: string) => void
}): ChatStorageLimit {
  const own = readMemberChatStorageLimit(args.member)
  if (own !== undefined) return own

  const overrides = args.roleOverrides ?? {}
  if (Object.prototype.hasOwnProperty.call(overrides, args.role)) {
    const entry = overrides[args.role]
    if (entry && Object.prototype.hasOwnProperty.call(entry, 'limitBytes')) {
      // `null` here is an override TO unlimited and must survive — falling
      // through would re-apply the shipped default. `readRoleChatStorageLimit`
      // keeps that distinction; the member column's reader deliberately
      // cannot. (The old code also had a dead `=== null` re-check after this,
      // unreachable because the shared reader already returned null.)
      const v = readRoleChatStorageLimit(entry.limitBytes)
      if (v !== undefined) return v
    }
  }

  if (!Object.prototype.hasOwnProperty.call(DEFAULT_ROLE_CHAT_STORAGE, args.role)) {
    args.onUnknownRole?.(args.role)
    return 0
  }
  return DEFAULT_ROLE_CHAT_STORAGE[args.role]
}

/** Is this member's limit set individually (the `CUSTOM` state)? */
export function hasOwnChatStorageLimit(member?: string | number | null): boolean {
  return readMemberChatStorageLimit(member) !== undefined
}

// ─── The refusal ─────────────────────────────────────────────────────────

/** `code` on {@link MemberChatStorageExceededError}; also the JSON `code`. */
export const MEMBER_CHAT_STORAGE_EXCEEDED_CODE = 'MEMBER_CHAT_STORAGE_EXCEEDED'

/**
 * What a member is told when their own chat-storage limit is in the way.
 *
 * Says whose limit it is, because the member will otherwise read it as the
 * company being full and go asking the wrong question. "Ask an admin" is the
 * real next step: unlike a full drive, a member cannot always clear enough —
 * an admin raising their limit is the other half of the answer, and the Adjust
 * limit dialog exists for exactly this request.
 */
export function memberChatStorageMessage(used: number, limit: number): string {
  return `You've used ${formatStorageBytes(used)} of your ${formatStorageBytes(limit)} chat storage. `
    + 'Delete some uploaded files, or ask an admin to raise your limit.'
}

/**
 * Thrown when a chat upload would take ONE MEMBER over their own chat-storage
 * limit, even though the organisation has room.
 *
 * ── A separate error from StorageQuotaExceededError on purpose ───────────
 *
 * They are two different problems with two different fixes. A full
 * organisation is fixed by the admin buying space or the company clearing the
 * Drive; a member at their own limit is fixed by that member deleting their
 * uploads or an admin raising their one number. Collapsing both into
 * STORAGE_QUOTA_EXCEEDED would send a member whose colleagues have 900 GB free
 * to go ask about the company plan, which is the wrong conversation.
 *
 * ── Matched by `name`, so the name is load-bearing ──────────────────────
 *
 * `apps/api/src/middleware/error.ts` maps it to **413** by `err.name`, the
 * same seam `StorageQuotaExceededError` and `TenantWriteFrozenError` use so
 * the middleware need not import this package. Renaming the class silently
 * turns every refused upload into an opaque 500.
 *
 * ── The figures are ALWAYS attached, unlike the tenant error ─────────────
 *
 * `StorageQuotaExceededError` omits its figures for a foreign tenant, because
 * a cross-tenant write is gated against someone else's ceiling. This limit is
 * always the CALLER'S OWN — a chat session lives in the tenant whose member is
 * uploading, and a member may see their own usage and their own limit on their
 * own storage page. There is no foreign case to protect, so there is no
 * optional-figures branch to forget.
 */
export class MemberChatStorageExceededError extends Error {
  readonly code = MEMBER_CHAT_STORAGE_EXCEEDED_CODE
  /** The member's counted chat storage at the time of refusal. */
  readonly usedBytes: number
  /** The member's effective limit — never `null` here: unlimited cannot refuse. */
  readonly limitBytes: number

  constructor(figures: { usedBytes: number; limitBytes: number }) {
    super(memberChatStorageMessage(figures.usedBytes, figures.limitBytes))
    // Set explicitly: subclassing Error does not reliably carry the
    // constructor name through transpilation, and the handler matches on it.
    this.name = 'MemberChatStorageExceededError'
    this.usedBytes = figures.usedBytes
    this.limitBytes = figures.limitBytes
  }
}

// ─────────────────────────────────────────────────────────────────────────
// What a chat-storage NOTIFICATION carries (#758 D26)
//
// Here rather than in `@papan/database` because it is pure arithmetic over
// two numbers, and putting it there meant the route tests — which mock the
// whole database module — had to either stub it (and then assert their own
// stub) or `importActual` the entire module including the schema. Neither
// is a good trade for a function that touches no database.
// ─────────────────────────────────────────────────────────────────────────

/**
 * THE FIGURES A CHAT-STORAGE NOTIFICATION CARRIES, derived once.
 *
 * #758 D26. These five were hand-written at THREE emit sites — the
 * per-member raise, the role-default raise and the decline — from three
 * differently-named row variables, against a payload type where every
 * field is optional. Omitting one, or omitting one ARM of one, compiled
 * and shipped.
 *
 * It did, repeatedly. Six consecutive review rounds each found a new
 * unguarded field or arm in this payload, and every one failed the same
 * two ways: fixed at one site and not the others, or one arm of a
 * two-armed expression tested and the other not. What a member read as a
 * result: "your limit is now 0 MB" under a green tick for a RAISE to
 * unlimited; "0 B free" for someone with 400 MB; "Your limit stays at the
 * same."; and, at a zero limit, an instruction to delete files that could
 * not possibly help.
 *
 * So: one derivation, and a return type where EVERY FIELD IS REQUIRED.
 * `limitLabel: string | undefined` rather than `limitLabel?: string` is
 * the whole point — a caller that forgets one does not compile, and the
 * unlimited arm exists in exactly one place instead of three.
 *
 * What it cannot prevent, stated plainly so nobody over-trusts it: a field
 * that is PRESENT and wrongly COMPUTED still compiles. Two of those six
 * rounds were exactly that. The difference is that there is now one place
 * to get it wrong and one test that pins it, instead of three.
 */
export interface ChatStorageNoticeFigures {
  /** The resulting limit is zero: nothing the member deletes can help. */
  blocked: boolean
  /** The resulting limit is `null`: there is no ceiling to name. */
  unlimited: boolean
  /** At or over a NONZERO limit — cannot attach, but clearing space works. */
  noHeadroom: boolean
  /** Absent when unlimited: there is no figure, and "0 B" would be a lie. */
  limitLabel: string | undefined
  freeLabel: string | undefined
}

/** Bytes as the notification copy renders them. The registry's strings are
 *  English-only by construction (see `notifications.ts`), so the figures are
 *  formatted here rather than carried raw for a client to localise. */
export function formatBytesForNotification(n: number): string {
  // KB BELOW A MEGABYTE, because `Math.round` turned every sub-512 KB figure
  // into "0 MB" — and 0 is the one number this feature is careful never to
  // say by accident: it means a hard block everywhere else. The reachable
  // case is the ASK: an admin's bell described a member blocked at 0 while
  // holding 300 KB as "0 MB of 0 MB", which reads as "she has used nothing".
  //
  // `Math.round` BEFORE the boundary test, or 1_048_575 bytes prints
  // "1024 KB" where the next arm would say "1.0 MB".
  if (n < 1024) return `${n} B`
  const kb = Math.round(n / 1024)
  if (kb < 1024) return `${kb} KB`
  const mb = n / (1024 * 1024)
  // TB too, or the same ceiling reads "16384.0 GB" in the bell and "16 TB"
  // in the chat banner, and 16 TiB is exactly what `parseChatStorageLimit`
  // allows.
  //
  // No PB arm, and the cap is NOT the reason. A LIMIT under 1 MB is not
  // reachable through the product, but a USED figure under 1 MB is the
  // common case for a blocked member, and nothing bounds what a member
  // with no limit in force accumulates. The honest reason is that TB is
  // the largest unit any deployment has needed.
  if (mb < 1024) return `${mb < 10 ? mb.toFixed(1) : Math.round(mb)} MB`
  const gb = mb / 1024
  if (gb < 1024) return `${gb.toFixed(1)} GB`
  return `${(gb / 1024).toFixed(1)} TB`
}

/**
 * Derive the five from a SETTLED member row — the one read after the stamp,
 * not the one read before it.
 *
 * `null` means the member is gone (removed between the two reads). Every
 * figure is then absent, which is honest: there is nobody to describe.
 */
export function chatStorageNoticeFigures(
  settled: { totalBytes: number; limitBytes: number | null } | null | undefined,
): ChatStorageNoticeFigures {
  if (!settled) {
    return { blocked: false, unlimited: false, noHeadroom: false, limitLabel: undefined, freeLabel: undefined }
  }
  const { totalBytes, limitBytes } = settled
  if (limitBytes === null) {
    // Unlimited: no ceiling, so no figures. NOT `blocked`, and not 0 —
    // sending "0 B" here is what made a member who had just been granted an
    // unlimited ceiling read "your limit is now 0 B".
    return { blocked: false, unlimited: true, noHeadroom: false, limitLabel: undefined, freeLabel: undefined }
  }
  return {
    blocked: limitBytes === 0,
    unlimited: false,
    // `>=`, not `>`: the gate refuses at `total + net > limit`, so holding
    // exactly the limit is the same state as being over it.
    noHeadroom: limitBytes > 0 && totalBytes >= limitBytes,
    limitLabel: formatBytesForNotification(limitBytes),
    // Clamped, or a member lowered below what they hold reads "-100 B free".
    freeLabel: formatBytesForNotification(Math.max(0, limitBytes - totalBytes)),
  }
}
